'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { ArrowLeft, AlertCircle, Loader2, Workflow } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import dynamic from 'next/dynamic';
import { useTranslation } from '@/hooks/useTranslation';
import { FormRenderer } from '@features/forms/components/FormRenderer';
import {
  getFormDefinitionByKey,
  getDesignData,
  createInstance,
  updateInstance,
  submitInstance,
  previewProcessWithApprovers,
  type FormDefinition,
  type FormVersion,
  type ProcessVersion,
  type PreviewProcessResponse,
} from '@/services/api/form-management';
import { ApiClientError } from '@/lib/api-client';

// 动态导入飞书风格流程预览（时间线样式）
const LarkProcessPreview = dynamic(
  () => import('@features/approval/designer/LarkProcessPreview'),
  { 
    ssr: false,
    loading: () => (
      <div className="h-[200px] flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    ),
  }
);

export default function FormSubmitPage() {
  const router = useRouter();
  const params = useParams();
  const formKey = params.formKey as string;
  const { t } = useTranslation();

  const [form, setForm] = useState<FormDefinition | null>(null);
  const [formVersion, setFormVersion] = useState<FormVersion | null>(null);
  const [processVersion, setProcessVersion] = useState<ProcessVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftInstanceId, setDraftInstanceId] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [calculatedProcess, setCalculatedProcess] = useState<PreviewProcessResponse | null>(null);
  const [loadingProcess, setLoadingProcess] = useState(false);

  // ⭐ 防重复提交标志（使用时间戳 + 布尔标志双重保护）
  const isSubmittingRef = useRef(false);
  const isSavingRef = useRef(false);
  const lastSubmitTimeRef = useRef(0);
  const lastSaveTimeRef = useRef(0);

  // 检查表单是否填写完整（复用 JSON Schema 验证逻辑）
  useEffect(() => {
    if (!formVersion?.schema) {
      setIsFormValid(false);
      return;
    }

    const schema = formVersion.schema as any;
    const { properties = {}, required = [] } = schema;
    
    // 1. 检查所有必填字段是否都有值
    const requiredFieldsValid = required.every((fieldName: string) => {
      const value = formData[fieldName];
      // 检查值是否存在且不为空
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });
    
    if (!requiredFieldsValid) {
      setIsFormValid(false);
      return;
    }
    
    // 2. 验证字段格式和约束（基于 JSON Schema）
    let hasFormatError = false;
    
    Object.keys(properties).forEach((fieldName) => {
      const fieldSchema = properties[fieldName];
      const fieldValue = formData[fieldName];
      
      // 跳过空值
      if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        return;
      }
      
      // 字符串类型验证
      if (fieldSchema.type === 'string' && typeof fieldValue === 'string') {
        // 最小长度
        if (fieldSchema.minLength && fieldValue.length < fieldSchema.minLength) {
          hasFormatError = true;
        }
        // 最大长度
        if (fieldSchema.maxLength && fieldValue.length > fieldSchema.maxLength) {
          hasFormatError = true;
        }
        // 正则表达式验证
        if (fieldSchema.pattern) {
          const regex = new RegExp(fieldSchema.pattern);
          if (!regex.test(fieldValue)) {
            hasFormatError = true;
          }
        }
        // 邮箱格式验证
        if (fieldSchema.format === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(fieldValue)) {
            hasFormatError = true;
          }
        }
      }
      
      // 数字类型验证
      if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
        const numValue = Number(fieldValue);
        // 最小值
        if (fieldSchema.minimum !== undefined && numValue < fieldSchema.minimum) {
          hasFormatError = true;
        }
        // 最大值
        if (fieldSchema.maximum !== undefined && numValue > fieldSchema.maximum) {
          hasFormatError = true;
        }
      }
      
      // 数组类型验证（子表单）
      if (fieldSchema.type === 'array' && Array.isArray(fieldValue)) {
        const itemSchema = fieldSchema.items as any;
        if (itemSchema?.type === 'object' && itemSchema.properties && itemSchema.required) {
          const itemRequired = itemSchema.required as string[];
          
          fieldValue.forEach((row: any) => {
            itemRequired.forEach((itemFieldName: string) => {
              const itemFieldValue = row?.[itemFieldName];
              const isEmpty = 
                itemFieldValue === undefined || 
                itemFieldValue === null || 
                (typeof itemFieldValue === 'string' && itemFieldValue.trim() === '') ||
                (typeof itemFieldValue === 'number' && isNaN(itemFieldValue));
              
              if (isEmpty) {
                hasFormatError = true;
              }
            });
          });
        }
      }
    });
    
    setIsFormValid(!hasFormatError);
  }, [formData, formVersion]);

  // 监听表单数据变化，动态计算审批人（带防抖）
  useEffect(() => {
    if (!isFormValid || !form || !processVersion) {
      setCalculatedProcess(null);
      return;
    }

    // 防抖：用户停止输入 500ms 后才计算
    const timer = setTimeout(async () => {
      try {
        setLoadingProcess(true);
        const result = await previewProcessWithApprovers(form.id, {
          formData,
          // TODO: 如果支持多部门用户，需要传入 departmentId
          // departmentId: selectedDepartmentId,
        });
        setCalculatedProcess(result);
      } catch (error) {
        console.error('[FormSubmit] Failed to calculate approvers:', error);
        // 失败时使用静态流程（降级处理）
        setCalculatedProcess(null);
      } finally {
        setLoadingProcess(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData, isFormValid, form, processVersion]);

  useEffect(() => {
    loadForm();
  }, [formKey]);

  const loadForm = async () => {
    try {
      setLoading(true);
      
      // 1. 先获取表单定义
      const formDef = await getFormDefinitionByKey(formKey);
      console.log('[FormSubmit] Form definition:', formDef);
      setForm(formDef);
      
      // 2. 获取设计数据（包含 schema、uiSchema 和 processVersion）
      const designData = await getDesignData(formDef.id);
      console.log('[FormSubmit] Design data:', designData);
      setFormVersion(designData.formVersion);
      setProcessVersion(designData.processVersion); // ⭐ 保存流程版本
      
      // 3. 初始化表单数据（从 schema 的默认值）
      if (designData.formVersion?.schema) {
        const initialData: Record<string, any> = {};
        const schema = designData.formVersion.schema;
        console.log('[FormSubmit] Schema:', schema);
        
        // 提取默认值
        if (schema.properties) {
          Object.entries(schema.properties).forEach(([key, prop]: [string, any]) => {
            if (prop.default !== undefined) {
              initialData[key] = prop.default;
            }
          });
        }
        
        setFormData(initialData);
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(`${t.forms.instance.loadFormFailed}: ${error.message}`);
      }
      // 返回审批中心
      setTimeout(() => router.push('/approvals'), 2000);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 保存草稿
   * ⭐ FormRenderer 会传入当前的表单数据
   */
  const handleSaveDraft = async (draftFormData: Record<string, any>) => {
    if (!form) return;

    // ⭐ 【零技术债 - 多重防护机制】
    const now = Date.now();
    
    // 1. 时间戳防护：2秒内不允许重复保存
    if (now - lastSaveTimeRef.current < 2000) {
      console.log('[FormSubmit] 保存太频繁，忽略点击（距上次保存 ' + (now - lastSaveTimeRef.current) + 'ms）');
      return;
    }
    
    // 2. 状态标志防护：检查是否已在进行中
    if (isSavingRef.current || isSubmittingRef.current) {
      console.log('[FormSubmit] 正在处理中，忽略重复点击', { 
        isSaving: isSavingRef.current, 
        isSubmitting: isSubmittingRef.current 
      });
      return;
    }

    // 3. 立即更新时间戳和标志
    lastSaveTimeRef.current = now;
    isSavingRef.current = true;

    try {
      setSaving(true);
      
      console.log('[FormSubmit] Saving draft with formId:', form.id);
      console.log('[FormSubmit] Form data:', draftFormData);
      
      // 如果已有草稿ID，更新草稿；否则创建新草稿
      if (draftInstanceId) {
        await updateInstance(draftInstanceId, { formData: draftFormData });
        toast.success(t.forms.instance.draftUpdated);
      } else {
        const response = await createInstance({
          formId: form.id,
          data: draftFormData,
        });
        
        console.log('[FormSubmit] Created draft response:', response);
        
        if (!response || !response.formInstance || !response.formInstance.id) {
          throw new Error(t.forms.instance.createDraftFailed);
        }
        
        setDraftInstanceId(response.formInstance.id);
        toast.success(t.forms.instance.draftSaved);
      }
      
    } catch (error) {
      console.error('[FormSubmit] Save draft error:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.forms.instance.saveFailed}: ${error.message}`);
      } else {
        toast.error(`${t.forms.instance.saveFailed}: ${error instanceof Error ? error.message : t.forms.instance.unknownError}`);
      }
    } finally {
      isSavingRef.current = false;  // ⭐ 重置标志
      setSaving(false);
    }
  };

  /**
   * 提交表单（启动审批流程）
   * ⭐ 这个函数会在 FormRenderer 完成验证后被调用
   */
  const handleSubmit = async (validatedFormData: Record<string, any>) => {
    if (!form) return;

    // ⭐ 【零技术债 - 多重防护机制】
    const now = Date.now();
    
    // 1. 时间戳防护：2秒内不允许重复提交（防止快速双击）
    if (now - lastSubmitTimeRef.current < 2000) {
      console.log('[FormSubmit] 提交太频繁，忽略点击（距上次提交 ' + (now - lastSubmitTimeRef.current) + 'ms）');
      return;
    }
    
    // 2. 状态标志防护：检查是否已在进行中
    if (isSubmittingRef.current || isSavingRef.current) {
      console.log('[FormSubmit] 正在处理中，忽略重复点击', { 
        isSubmitting: isSubmittingRef.current, 
        isSaving: isSavingRef.current 
      });
      return;
    }

    // 3. 立即更新时间戳和标志
    lastSubmitTimeRef.current = now;
    isSubmittingRef.current = true;

    try {
      setSubmitting(true);
      
      console.log('[FormSubmit] Submitting form with formId:', form.id);
      console.log('[FormSubmit] Form data:', validatedFormData);
      
      let instanceId: string;
      
      // 1. 如果已有草稿ID，直接提交；否则先创建草稿
      if (draftInstanceId) {
        instanceId = draftInstanceId;
        // 更新草稿数据
        await updateInstance(instanceId, { formData: validatedFormData });
      } else {
        // 创建草稿
        const response = await createInstance({
          formId: form.id,
          data: validatedFormData,
        });
        
        console.log('[FormSubmit] Created instance response:', response);
        
        if (!response || !response.formInstance || !response.formInstance.id) {
          throw new Error(t.forms.instance.createInstanceFailed);
        }
        
        instanceId = response.formInstance.id;
      }
      
      // 2. 提交实例（启动审批流程）
      console.log('[FormSubmit] Submitting instance:', instanceId);
      await submitInstance(instanceId);
      
      toast.success(t.forms.instance.submitSuccess);
      
      // 跳转到审批中心的"我创建的"标签页
      setTimeout(() => {
        router.push('/approvals?tab=initiated');
      }, 1500);
    } catch (error) {
      console.error('[FormSubmit] Submit error:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.forms.instance.submitFailed}: ${error.message}`);
      } else {
        toast.error(`${t.forms.instance.submitFailed}: ${error instanceof Error ? error.message : t.forms.instance.unknownError}`);
      }
    } finally {
      isSubmittingRef.current = false;  // ⭐ 重置标志
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t.forms.instance.loadingForm}</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.forms.instance.formNotFound}</h2>
          <p className="text-gray-600 mb-4">{t.forms.instance.formNotFoundDesc}</p>
          <Button onClick={() => router.push('/approvals')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.forms.instance.backToApproval}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push('/approvals')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.forms.instance.back}
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{form.name}</h1>
                {form.description && (
                  <p className="text-xs text-gray-500">{form.description}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 表单内容 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {formVersion?.schema ? (
            <FormRenderer
              schema={formVersion.schema as any}
              uiSchema={(formVersion.uiSchema || {}) as any}
              formData={formData}
              onChange={setFormData}
              onSubmit={handleSubmit}
              onSave={handleSaveDraft}
              submitButtonText={t.forms.instance.actions.submit}
              saveButtonText={t.forms.instance.saveDraft}
              disabled={saving || submitting}
            />
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t.forms.instance.formIncomplete}</p>
            </div>
          )}
        </div>

        {/* ⭐ 审批流程预览区域 */}
        {processVersion?.model && processVersion.model.nodes && processVersion.model.nodes.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-testid="approval-process-preview">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Workflow className="w-5 h-5 text-blue-500" />
              {t.forms.instance.approvalProcess}
            </h3>
            
            {isFormValid ? (
              <>
                <div className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                  {loadingProcess ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                      <p className="text-sm text-gray-500">{t.forms.instance.loadingProcessPreview || '计算审批人中...'}</p>
                    </div>
                  ) : (
                    <LarkProcessPreview
                      model={calculatedProcess?.model || processVersion.model}
                      showInitiator={true}
                      initiatorName={calculatedProcess?.initiator?.name || t.forms.instance.currentUser}
                      showEndNode={true}
                      compact={false}
                    />
                  )}
                </div>
                {!loadingProcess && (
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {t.forms.instance.processPreviewHint}
                  </p>
                )}
              </>
            ) : (
              <div className="border border-gray-100 rounded-lg p-8 bg-gray-50/50 text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 text-sm">
                  {t.forms.instance.processPreviewIncomplete}
                </p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

